import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userAPI, analyticsAPI, contentAPI, rewardAPI, commissionAPI, messageAPI, taskAPI, storageAPI } from '../services/api';
import { Shield, Users, FileCheck, Gift, BarChart3, Plus, Package, DollarSign, Megaphone, ClipboardList, Activity, Download, CheckSquare, Settings, TrendingUp, UploadCloud } from 'lucide-react';
import Pagination from '../components/Pagination';
import { SkeletonTable } from '../components/LoadingSkeleton';
import './Admin.css';

function Admin() {
  const [tab, setTab] = useState('users');
  const queryClient = useQueryClient();

  // ── analytics ──
  const { data: analytics } = useQuery({
    queryKey: ['admin-analytics'],
    queryFn: () => analyticsAPI.getDashboard().then(r => r.data),
  });

  // ── pending submissions badge ──
  const { data: pendingData } = useQuery({
    queryKey: ['pending-submissions'],
    queryFn: () => contentAPI.getPendingSubmissions().then(r => r.data.submissions),
  });
  const pendingCount = pendingData?.length || 0;

  // ── pending redemptions badge ──
  const { data: redemptionsData } = useQuery({
    queryKey: ['admin-redemptions'],
    queryFn: () => rewardAPI.adminGetRedemptions().then(r => r.data.redemptions),
  });
  const pendingRedemptionCount = redemptionsData?.filter(r => r.status === 'pending').length || 0;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1><Shield size={28} /> Admin Panel</h1>
        <p>Manage users, content, rewards, commissions, and view analytics.</p>
      </div>

      <div className="admin-tabs">
        <button className={`admin-tab ${tab === 'users' ? 'active' : ''}`} onClick={() => setTab('users')}>
          <Users size={16} /> Users
        </button>
        <button className={`admin-tab ${tab === 'content' ? 'active' : ''}`} onClick={() => setTab('content')}>
          <FileCheck size={16} /> Content
          {pendingCount > 0 && <span className="tab-badge">{pendingCount}</span>}
        </button>
        <button className={`admin-tab ${tab === 'redemptions' ? 'active' : ''}`} onClick={() => setTab('redemptions')}>
          <Package size={16} /> Redemptions
          {pendingRedemptionCount > 0 && <span className="tab-badge">{pendingRedemptionCount}</span>}
        </button>
        <button className={`admin-tab ${tab === 'commissions' ? 'active' : ''}`} onClick={() => setTab('commissions')}>
          <DollarSign size={16} /> Commissions
        </button>
        <button className={`admin-tab ${tab === 'rewards' ? 'active' : ''}`} onClick={() => setTab('rewards')}>
          <Gift size={16} /> Rewards
        </button>
        <button className={`admin-tab ${tab === 'tasks' ? 'active' : ''}`} onClick={() => setTab('tasks')}>
          <ClipboardList size={16} /> Tasks
        </button>
        <button className={`admin-tab ${tab === 'broadcast' ? 'active' : ''}`} onClick={() => setTab('broadcast')}>
          <Megaphone size={16} /> Broadcast
        </button>
        <button className={`admin-tab ${tab === 'media' ? 'active' : ''}`} onClick={() => setTab('media')}>
          <UploadCloud size={16} /> Media
        </button>
        <button className={`admin-tab ${tab === 'activity' ? 'active' : ''}`} onClick={() => setTab('activity')}>
          <Activity size={16} /> Activity
        </button>
        <button className={`admin-tab ${tab === 'analytics' ? 'active' : ''}`} onClick={() => setTab('analytics')}>
          <BarChart3 size={16} /> Analytics
        </button>
        <button className={`admin-tab ${tab === 'charts' ? 'active' : ''}`} onClick={() => setTab('charts')}>
          <TrendingUp size={16} /> Charts
        </button>
        <button className={`admin-tab ${tab === 'settings' ? 'active' : ''}`} onClick={() => setTab('settings')}>
          <Settings size={16} /> Settings
        </button>
      </div>

      {tab === 'users' && <UsersTab />}
      {tab === 'content' && <ContentTab />}
      {tab === 'redemptions' && <RedemptionsTab />}
      {tab === 'commissions' && <CommissionsTab />}
      {tab === 'rewards' && <RewardsTab />}
      {tab === 'tasks' && <TasksTab />}
      {tab === 'broadcast' && <BroadcastTab />}
      {tab === 'media' && <MediaTab />}
      {tab === 'activity' && <ActivityTab />}
      {tab === 'analytics' && <AnalyticsTab analytics={analytics} />}
      {tab === 'charts' && <ChartsTab />}
      {tab === 'settings' && <SettingsTab />}
    </div>
  );
}

function FolderPicker({ onChange }) {
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.webkitdirectory = true;
    }
  }, []);

  return (
    <input
      ref={inputRef}
      type="file"
      onChange={onChange}
      className="media-file-input"
      multiple
    />
  );
}

/* ═══════════ MEDIA TAB ═══════════ */
function MediaTab() {
  const queryClient = useQueryClient();
  const [files, setFiles] = useState([]);
  const [uploadMode, setUploadMode] = useState('files'); // 'files' | 'folder'
  const [uploadProgress, setUploadProgress] = useState({}); // { [fileName]: percentage }
  const [uploadedFileUrls, setUploadedFileUrls] = useState([]);
  const [error, setError] = useState('');
  const [uploadErrors, setUploadErrors] = useState({});
  const [pendingUploads, setPendingUploads] = useState(0);
  const [completedUploads, setCompletedUploads] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null); // Can be a file or a folder

  // Folder and navigation state
  const [prefix, setPrefix] = useState('');

  // Sorting, filtering, and search state
  const [sortBy, setSortBy] = useState('key');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filterByType, setFilterByType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  React.useEffect(() => {
    if (pendingUploads > 0 && completedUploads === pendingUploads) {
      const timer = setTimeout(() => {
        setFiles([]);
        setUploadedFileUrls([]);
        setUploadErrors({});
        setUploadProgress({});
        setPendingUploads(0);
        setCompletedUploads(0);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [pendingUploads, completedUploads]);

  const { data: filesResponse, isLoading: isLoadingFiles } = useQuery({
    queryKey: ['storage-files', prefix, sortBy, sortOrder, filterByType, debouncedSearchTerm],
    queryFn: () => storageAPI.getFiles({ prefix, sortBy, sortOrder, filterByType, search: debouncedSearchTerm }).then(r => r.data),
  });
  const allItems = filesResponse?.files || [];
  const folders = allItems.filter(item => item.type === 'folder');
  const items = allItems.filter(item => item.type !== 'folder');

  const uploadMutation = useMutation({
    mutationFn: ({ file, prefix }) => {
      const progressCallback = (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(prev => ({ ...prev, [file.name]: percentCompleted }));
      };
      return storageAPI.uploadFile(file, progressCallback, prefix);
    },
    onSuccess: (response, variables) => {
      setUploadedFileUrls(prev => [...prev, response.data.url]);
      queryClient.invalidateQueries({ queryKey: ['storage-files', prefix] });
      setCompletedUploads(prev => prev + 1);
    },
    onError: (error, variables) => {
      const { file } = variables;
      setUploadErrors(prev => ({ ...prev, [file.name]: 'File upload failed. Please try again.' }));
      setCompletedUploads(prev => prev + 1);
    },
  });

  const createFolderMutation = useMutation({
    mutationFn: (folderName) => {
      const fullPrefix = prefix ? `${prefix}${folderName}/` : `${folderName}/`;
      return storageAPI.createFolder(fullPrefix);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storage-files', prefix] });
    },
    onError: (error) => {
      setError(error.response?.data?.error || 'Folder creation failed.');
    }
  });

  const deleteFileMutation = useMutation({
    mutationFn: (key) => storageAPI.deleteFile(key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storage-files', prefix] });
      setShowConfirmModal(false);
      setItemToDelete(null);
    },
    onError: (error) => {
      setError('File deletion failed. Please try again.');
      setShowConfirmModal(false);
    },
  });

  const deleteFolderMutation = useMutation({
    mutationFn: (folderPrefix) => storageAPI.deleteFolder(folderPrefix),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storage-files', prefix] });
      setShowConfirmModal(false);
      setItemToDelete(null);
    },
    onError: (error) => {
      setError(error.response?.data?.error || 'Folder deletion failed.');
      setShowConfirmModal(false);
    },
  });


  const handleFileChange = (event) => {
    setFiles(Array.from(event.target.files));
    setUploadedFileUrls([]);
    setUploadErrors({});
    setUploadProgress({});
    setError('');
  };

  const handleUpload = () => {
    if (files.length === 0) return;
    setUploadedFileUrls([]);
    setUploadErrors({});
    setUploadProgress({});
    setError('');
    setPendingUploads(files.length);
    setCompletedUploads(0);
    files.forEach(file => {
      let filePrefix = prefix;
      if (file.webkitRelativePath) {
        const parts = file.webkitRelativePath.split('/');
        parts.pop(); // remove filename — keep only directory segments
        const relativeDir = parts.join('/');
        filePrefix = prefix + (relativeDir ? relativeDir + '/' : '');
      }
      uploadMutation.mutate({ file, prefix: filePrefix });
    });
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowConfirmModal(true);
  };

  const confirmDelete = () => {
    if (!itemToDelete) return;

    if (itemToDelete.isFolder) {
      deleteFolderMutation.mutate(itemToDelete.prefix);
    } else {
      deleteFileMutation.mutate(itemToDelete.key);
    }
  };

  const handleCreateFolder = () => {
    const folderName = window.prompt('Enter folder name:');
    if (folderName) {
      createFolderMutation.mutate(folderName);
    }
  };

  const handlePrefixChange = (newPrefix) => {
    setPrefix(newPrefix);
  };

  const Breadcrumbs = () => {
    const parts = prefix.split('/').filter(Boolean);
    const crumbs = [{ label: 'Media', path: '' }];
    parts.forEach((part, index) => {
      const path = parts.slice(0, index + 1).join('/') + '/';
      crumbs.push({ label: part, path });
    });

    return (
      <div className="media-breadcrumbs">
        {crumbs.map((crumb, index) => (
          <span key={index}>
            <button onClick={() => handlePrefixChange(crumb.path)}>{crumb.label}</button>
            {index < crumbs.length - 1 && ' / '}
          </span>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="admin-card">
        <div className="admin-card-header">
          <h2><UploadCloud size={20} /> Media Upload</h2>
        </div>
        <div style={{ padding: 20 }}>
          <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 16 }}>
            Upload media assets to the current folder: <strong>{prefix || '/'}</strong>
          </p>
          <div className="admin-form-group">
            <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontWeight: uploadMode === 'files' ? 600 : 400 }}>
                <input
                  type="radio"
                  name="uploadMode"
                  value="files"
                  checked={uploadMode === 'files'}
                  onChange={() => { setUploadMode('files'); setFiles([]); setUploadedFileUrls([]); setUploadErrors({}); }}
                />
                Upload Files
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontWeight: uploadMode === 'folder' ? 600 : 400 }}>
                <input
                  type="radio"
                  name="uploadMode"
                  value="folder"
                  checked={uploadMode === 'folder'}
                  onChange={() => { setUploadMode('folder'); setFiles([]); setUploadedFileUrls([]); setUploadErrors({}); }}
                />
                Upload Folder
              </label>
            </div>
            {uploadMode === 'files' ? (
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                className="media-file-input"
              />
            ) : (
              <FolderPicker onChange={handleFileChange} />
            )}
          </div>
          {files.length > 0 && (
            <div className="media-upload-preview">
              <p>Selected {files.length} file(s):</p>
              <ul style={{ listStyle: 'none', paddingLeft: 0, fontSize: '12px' }}>
                {files.map((f, i) => <li key={i}>{f.name} ({(f.size / 1024 / 1024).toFixed(2)} MB)</li>)}
              </ul>
            </div>
          )}
          <div className="admin-form-actions">
            <button onClick={handleUpload} className="admin-btn-primary" disabled={files.length === 0 || uploadMutation.isPending}>
              {uploadMutation.isPending ? 'Uploading...' : `Upload ${files.length || ''} File(s)`}
            </button>
          </div>

          {error && <div className="broadcast-error" style={{marginTop: '16px'}}>{error}</div>}

          {uploadMutation.isPending && Object.keys(uploadProgress).length > 0 && (
            <div className="media-upload-progress" style={{ marginTop: '16px' }}>
              {files.map((file, index) => (
                <div key={index} className="progress-item" style={{ marginBottom: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                    <span>{file.name}</span>
                    <span>{uploadProgress[file.name] ? `${uploadProgress[file.name]}%` : 'waiting...'}</span>
                  </div>
                  <div className="media-progress-bar">
                    <div style={{ width: `${uploadProgress[file.name] || 0}%` }}></div>
                  </div>
                  {uploadErrors[file.name] && <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{uploadErrors[file.name]}</div>}
                </div>
              ))}
            </div>
          )}

          {uploadedFileUrls.length > 0 && !uploadMutation.isPending && (
            <div className="broadcast-success">
              Successfully uploaded {uploadedFileUrls.length} file(s)!
              <div className="media-uploaded-url">
                {uploadedFileUrls.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', fontSize: '12px' }}>{url}</a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="admin-card" style={{ marginTop: 20 }}>
        <div className="admin-card-header">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <h2><Package size={20} /> Uploaded Files</h2>
            <Breadcrumbs />
          </div>
          <div className="media-filters">
            <button className="admin-btn-primary" onClick={handleCreateFolder} disabled={createFolderMutation.isPending}>
              <Plus size={16} /> Create Folder
            </button>
            <input
              type="text"
              placeholder="Search files..."
              className="admin-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select className="admin-select" value={filterByType} onChange={(e) => setFilterByType(e.target.value)}>
              <option value="all">All Types</option>
              <option value="image">Images</option>
              <option value="video">Videos</option>
              <option value="document">Documents</option>
              <option value="other">Other</option>
            </select>
            <select className="admin-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="last_modified">Sort by Date</option>
              <option value="key">Sort by Name</option>
              <option value="size">Sort by Size</option>
            </select>
            <button className="admin-btn" onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}>
              {sortOrder === 'asc' ? '↑ Asc' : '↓ Desc'}
            </button>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>File Name</th>
                <th>Size</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingFiles ? (
                <tr>
                  <td colSpan="4" style={{ textAlign: 'center' }}>Loading...</td>
                </tr>
              ) : (folders.length === 0 && items.length === 0) ? (
                <tr>
                  <td colSpan="4" style={{ textAlign: 'center' }}>This folder is empty.</td>
                </tr>
              ) : (
                <>
                  {folders.map(f => (
                    <tr key={f.key}>
                      <td style={{ fontWeight: 500 }}>
                        <a href="#" onClick={(e) => { e.preventDefault(); handlePrefixChange(f.key); }}>
                          📁 {f.key.replace(prefix, '').replace('/', '')}
                        </a>
                      </td>
                      <td>--</td>
                      <td>--</td>
                      <td>
                        <button className="admin-btn-danger" onClick={() => handleDeleteClick({ ...f, isFolder: true, prefix: f.key })} disabled={deleteFolderMutation.isPending}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {items.map(f => (
                    <tr key={f.key}>
                      <td style={{ fontWeight: 500 }}><a href={f.url} target="_blank" rel="noopener noreferrer">{f.key.replace(prefix, '')}</a></td>
                      <td>{(f.size / 1024).toFixed(2)} KB</td>
                      <td style={{ fontSize: 13, color: '#6b7280' }}>{new Date(f.last_modified).toLocaleDateString()}</td>
                      <td>
                        <button className="admin-btn-danger" onClick={() => handleDeleteClick(f)} disabled={deleteFileMutation.isPending}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showConfirmModal && (
        <div className="admin-modal-overlay" onClick={() => setShowConfirmModal(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <h2>Confirm Deletion</h2>
            <p>
              Are you sure you want to delete{' '}
              <strong>{itemToDelete?.isFolder ? itemToDelete.prefix : itemToDelete?.key}</strong>?
              {itemToDelete?.isFolder && ' This will delete all files and subfolders inside.'} This action cannot be undone.
            </p>
            <div className="admin-form-actions">
              <button type="button" className="admin-btn" style={{ background: '#f3f4f6', color: '#374151' }} onClick={() => setShowConfirmModal(false)}>Cancel</button>
              <button type="button" className="admin-btn-danger" onClick={confirmDelete} disabled={deleteFileMutation.isPending || deleteFolderMutation.isPending}>
                {(deleteFileMutation.isPending || deleteFolderMutation.isPending) ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// A custom hook for debouncing a value
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

/* ═══════════ USERS TAB ═══════════ */
function UsersTab() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const { data: usersResponse, isLoading } = useQuery({
    queryKey: ['admin-users', page, limit],
    queryFn: () => userAPI.getAll({ page, limit }).then(r => r.data),
    keepPreviousData: true,
  });
  const users = usersResponse?.users || [];
  const pagination = usersResponse?.pagination;

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => userAPI.adminUpdate(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  const handleChange = (userId, field, value) => {
    updateMutation.mutate({ id: userId, data: { [field]: value } });
  };

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    if (selected.length === users?.length) setSelected([]);
    else setSelected(users?.map(u => u.id) || []);
  };

  const bulkUpdate = async (field, value) => {
    for (const id of selected) {
      await userAPI.adminUpdate(id, { [field]: value });
    }
    setSelected([]);
    queryClient.invalidateQueries({ queryKey: ['admin-users'] });
  };

  const handleExportUsers = () => {
    const token = localStorage.getItem('auth-storage');
    if (token) {
      const { state } = JSON.parse(token);
      window.open(`${analyticsAPI.exportUsers()}&token=${state.token}`, '_blank');
    }
  };

  if (isLoading) return <SkeletonTable rows={8} columns={9} />;

  const pendingUsers = users?.filter(u => u.status === 'pending') || [];
  const allUsers = users || [];

  return (
    <>
      {/* ── Pending Approvals Banner ── */}
      {pendingUsers.length > 0 && (
        <div className="admin-card pending-banner">
          <div className="admin-card-header">
            <h2>Pending Approvals ({pendingUsers.length})</h2>
          </div>
          <div className="pending-users-list">
            {pendingUsers.map(u => (
              <div key={u.id} className="pending-user-row">
                <div className="pending-user-info">
                  <div className="pending-user-avatar">{u.first_name?.[0]}{u.last_name?.[0]}</div>
                  <div>
                    <div style={{ fontWeight: 600 }}>{u.first_name} {u.last_name}</div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>{u.email}</div>
                  </div>
                  <div style={{ fontSize: 12, color: '#9ca3af', marginLeft: 'auto' }}>
                    Registered {new Date(u.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="pending-user-actions">
                  <button className="admin-btn admin-btn-approve" disabled={updateMutation.isPending}
                    onClick={() => handleChange(u.id, 'status', 'active')}>Approve</button>
                  <button className="admin-btn admin-btn-reject" disabled={updateMutation.isPending}
                    onClick={() => handleChange(u.id, 'status', 'suspended')}>Reject</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Bulk Actions Bar ── */}
      {selected.length > 0 && (
        <div className="bulk-actions-bar">
          <span>{selected.length} user{selected.length > 1 ? 's' : ''} selected</span>
          <button className="admin-btn admin-btn-approve" onClick={() => bulkUpdate('status', 'active')}>Activate All</button>
          <button className="admin-btn admin-btn-reject" onClick={() => bulkUpdate('status', 'suspended')}>Suspend All</button>
          <select className="admin-select" onChange={e => { if (e.target.value) bulkUpdate('tier', e.target.value); }} defaultValue="">
            <option value="" disabled>Change Tier...</option>
            <option value="bronze">Bronze</option>
            <option value="silver">Silver</option>
            <option value="gold">Gold</option>
            <option value="platinum">Platinum</option>
          </select>
          <button className="admin-btn" style={{ background: '#f3f4f6', color: '#374151' }} onClick={() => setSelected([])}>Clear</button>
        </div>
      )}

      {/* ── Full Users Table ── */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2>All Users ({pagination?.total || allUsers.length})</h2>
          <button className="admin-btn-export" onClick={handleExportUsers}><Download size={14} /> Export CSV</button>
        </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th><input type="checkbox" checked={selected.length === users?.length} onChange={toggleAll} /></th>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Tier</th>
              <th>Points</th>
              <th>Earnings</th>
              <th>Joined</th>
            </tr>
          </thead>
          <tbody>
            {users?.map(u => (
              <tr key={u.id} className={selected.includes(u.id) ? 'row-selected' : ''}>
                <td><input type="checkbox" checked={selected.includes(u.id)} onChange={() => toggleSelect(u.id)} /></td>
                <td style={{ fontWeight: 500 }}>{u.first_name} {u.last_name}</td>
                <td style={{ color: '#6b7280', fontSize: 13 }}>{u.email}</td>
                <td>
                  <select className="admin-select" value={u.role}
                    onChange={e => handleChange(u.id, 'role', e.target.value)}>
                    <option value="ambassador">Ambassador</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                    <option value="stakeholder">Stakeholder</option>
                  </select>
                </td>
                <td>
                  <select className="admin-select" value={u.status}
                    onChange={e => handleChange(u.id, 'status', e.target.value)}>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="suspended">Suspended</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </td>
                <td>
                  <select className="admin-select" value={u.tier}
                    onChange={e => handleChange(u.id, 'tier', e.target.value)}>
                    <option value="bronze">Bronze</option>
                    <option value="silver">Silver</option>
                    <option value="gold">Gold</option>
                    <option value="platinum">Platinum</option>
                  </select>
                </td>
                <td>{u.points || 0}</td>
                <td>${Number(u.total_earnings || 0).toFixed(2)}</td>
                <td style={{ fontSize: 13, color: '#6b7280' }}>{new Date(u.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    {pagination && (
      <Pagination
        page={page}
        totalPages={pagination.total_pages}
        total={pagination.total}
        limit={limit}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
      />
    )}
    </>
  );
}

/* ═══════════ CONTENT APPROVAL TAB ═══════════ */
function ContentTab() {
  const queryClient = useQueryClient();
  const [pointsMap, setPointsMap] = useState({});

  const { data: submissions, isLoading } = useQuery({
    queryKey: ['pending-submissions'],
    queryFn: () => contentAPI.getPendingSubmissions().then(r => r.data.submissions),
  });

  const verifyMutation = useMutation({
    mutationFn: ({ id, data }) => contentAPI.verifySubmission(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pending-submissions'] }),
  });

  const handleVerify = (id, status) => {
    verifyMutation.mutate({
      id,
      data: {
        status,
        points: status === 'approved' ? parseInt(pointsMap[id]) || 10 : 0,
      },
    });
  };

  if (isLoading) return <div className="admin-loading">Loading submissions...</div>;

  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <h2>Pending Submissions ({submissions?.length || 0})</h2>
      </div>
      {!submissions || submissions.length === 0 ? (
        <div className="admin-empty">No pending submissions to review.</div>
      ) : (
        submissions.map(s => (
          <div key={s.id} className="submission-card">
            <div className="submission-info">
              <h4>{s.title}</h4>
              <div className="sub-meta">
                <span>By: {s.first_name} {s.last_name}</span>
                <span>{s.media_type}</span>
                {s.campaign_title && <span className="sub-campaign-badge">{s.campaign_title}</span>}
                <span>{new Date(s.created_at).toLocaleDateString()}</span>
              </div>
              {s.caption && <p style={{ fontSize: 13, color: '#374151', marginTop: 6 }}>{s.caption}</p>}
              {s.media_url && (
                <a href={s.media_url} target="_blank" rel="noopener noreferrer" className="sub-url">
                  {s.media_url}
                </a>
              )}
            </div>
            <div className="submission-actions">
              <input
                type="number"
                className="points-input"
                placeholder="Pts"
                value={pointsMap[s.id] || ''}
                onChange={e => setPointsMap(p => ({ ...p, [s.id]: e.target.value }))}
              />
              <button className="admin-btn admin-btn-approve"
                disabled={verifyMutation.isPending}
                onClick={() => handleVerify(s.id, 'approved')}>
                Approve
              </button>
              <button className="admin-btn admin-btn-reject"
                disabled={verifyMutation.isPending}
                onClick={() => handleVerify(s.id, 'rejected')}>
                Reject
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

/* ═══════════ REDEMPTIONS TAB ═══════════ */
function RedemptionsTab() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('all');

  const { data: redemptions, isLoading } = useQuery({
    queryKey: ['admin-redemptions'],
    queryFn: () => rewardAPI.adminGetRedemptions().then(r => r.data.redemptions),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => rewardAPI.adminUpdateRedemption(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-redemptions'] }),
  });

  if (isLoading) return <div className="admin-loading">Loading redemptions...</div>;

  const filtered = statusFilter === 'all'
    ? redemptions
    : redemptions?.filter(r => r.status === statusFilter);

  const statusCounts = {};
  redemptions?.forEach(r => { statusCounts[r.status] = (statusCounts[r.status] || 0) + 1; });

  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <h2>Reward Redemptions ({redemptions?.length || 0})</h2>
      </div>

      {/* Status filters */}
      <div className="redemption-filters">
        {['all', 'pending', 'processing', 'shipped', 'delivered', 'cancelled'].map(s => (
          <button
            key={s}
            className={`filter-chip ${statusFilter === s ? 'active' : ''}`}
            onClick={() => setStatusFilter(s)}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
            {s !== 'all' && statusCounts[s] ? ` (${statusCounts[s]})` : s === 'all' ? ` (${redemptions?.length || 0})` : ''}
          </button>
        ))}
      </div>

      {!filtered || filtered.length === 0 ? (
        <div className="admin-empty">No redemptions found.</div>
      ) : (
        filtered.map(r => (
          <div key={r.id} className="redemption-card">
            <div className="redemption-header">
              <div>
                <h4>{r.reward_title}</h4>
                <span className={`badge badge-${r.reward_type === 'gift_card' ? 'active' : 'ambassador'}`}>
                  {r.reward_type?.replace('_', ' ')}
                </span>
              </div>
              <span className={`badge badge-${r.status}`}>{r.status}</span>
            </div>
            <div className="redemption-details">
              <div className="redemption-user">
                <strong>{r.first_name} {r.last_name}</strong>
                <span style={{ color: '#6b7280', fontSize: 13 }}>{r.email}</span>
              </div>
              <div className="redemption-meta">
                <span><strong>{r.points_spent}</strong> pts</span>
                <span>{new Date(r.created_at).toLocaleDateString()}</span>
                {r.tracking_number && <span>Tracking: {r.tracking_number}</span>}
              </div>
            </div>
            {r.shipping_info && (
              <div className="redemption-shipping">
                Shipping: {typeof r.shipping_info === 'string' ? r.shipping_info : JSON.stringify(r.shipping_info)}
              </div>
            )}
            {r.status !== 'delivered' && r.status !== 'cancelled' && (
              <div className="redemption-actions">
                {r.status === 'pending' && (
                  <>
                    <button
                      className="admin-btn admin-btn-approve"
                      disabled={updateMutation.isPending}
                      onClick={() => updateMutation.mutate({ id: r.id, data: { status: 'processing' } })}>
                      Start Processing
                    </button>
                    <button
                      className="admin-btn admin-btn-reject"
                      disabled={updateMutation.isPending}
                      onClick={() => {
                        if (window.confirm('Cancel and refund points?'))
                          updateMutation.mutate({ id: r.id, data: { status: 'cancelled' } });
                      }}>
                      Cancel &amp; Refund
                    </button>
                  </>
                )}
                {r.status === 'processing' && (
                  <>
                    <TrackingInput
                      onShip={(tracking) => updateMutation.mutate({ id: r.id, data: { status: 'shipped', tracking_number: tracking } })}
                      isPending={updateMutation.isPending}
                    />
                    <button
                      className="admin-btn admin-btn-reject"
                      disabled={updateMutation.isPending}
                      onClick={() => {
                        if (window.confirm('Cancel and refund points?'))
                          updateMutation.mutate({ id: r.id, data: { status: 'cancelled' } });
                      }}>
                      Cancel &amp; Refund
                    </button>
                  </>
                )}
                {r.status === 'shipped' && (
                  <button
                    className="admin-btn admin-btn-approve"
                    disabled={updateMutation.isPending}
                    onClick={() => updateMutation.mutate({ id: r.id, data: { status: 'delivered' } })}>
                    Mark Delivered
                  </button>
                )}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

function TrackingInput({ onShip, isPending }) {
  const [tracking, setTracking] = useState('');
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input className="points-input" style={{ width: 140 }} placeholder="Tracking #"
        value={tracking} onChange={e => setTracking(e.target.value)} />
      <button className="admin-btn admin-btn-approve" disabled={isPending} onClick={() => onShip(tracking)}>Mark Shipped</button>
    </div>
  );
}

/* ═══════════ COMMISSIONS TAB ═══════════ */
function CommissionsTab() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('all');

  const { data: sales, isLoading } = useQuery({
    queryKey: ['admin-sales'],
    queryFn: () => commissionAPI.adminGetSales().then(r => r.data.sales),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => commissionAPI.adminUpdateSale(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-sales'] }),
  });

  if (isLoading) return <div className="admin-loading">Loading sales...</div>;

  const filtered = filter === 'all' ? sales : sales?.filter(s => s.payout_status === filter);
  const pendingPayout = sales?.filter(s => s.status === 'confirmed' && s.payout_status === 'pending') || [];
  const totalPending = pendingPayout.reduce((sum, s) => sum + Number(s.commission_amount || 0), 0);

  return (
    <>
      {pendingPayout.length > 0 && (
        <div className="admin-stats" style={{ marginBottom: 16 }}>
          <div className="admin-stat-card">
            <div className="admin-stat-icon" style={{ background: '#fef3c7' }}><DollarSign size={22} color="#f59e0b" /></div>
            <div>
              <div className="admin-stat-val">{pendingPayout.length}</div>
              <div className="admin-stat-lbl">Pending Payouts</div>
            </div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-icon" style={{ background: '#fce7f3' }}><DollarSign size={22} color="#ec4899" /></div>
            <div>
              <div className="admin-stat-val">${totalPending.toFixed(2)}</div>
              <div className="admin-stat-lbl">Total Owed</div>
            </div>
          </div>
        </div>
      )}
      <div className="admin-card">
        <div className="admin-card-header">
          <h2>Sales & Commissions ({sales?.length || 0})</h2>
          <button className="admin-btn-export" onClick={() => {
            const token = localStorage.getItem('auth-storage');
            if (token) { const { state } = JSON.parse(token); window.open(analyticsAPI.exportSales(), '_blank'); }
          }}><Download size={14} /> Export CSV</button>
        </div>
        <div className="redemption-filters">
          {['all', 'pending', 'paid', 'cancelled'].map(s => (
            <button key={s} className={`filter-chip ${filter === s ? 'active' : ''}`} onClick={() => setFilter(s)}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        {!filtered || filtered.length === 0 ? (
          <div className="admin-empty">No sales found.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Ambassador</th>
                  <th>Order ID</th>
                  <th>Total</th>
                  <th>Commission</th>
                  <th>Status</th>
                  <th>Payout</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 500 }}>{s.first_name} {s.last_name}</td>
                    <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{s.order_id}</td>
                    <td>${Number(s.order_total || 0).toFixed(2)}</td>
                    <td style={{ fontWeight: 600, color: '#10b981' }}>${Number(s.commission_amount || 0).toFixed(2)}</td>
                    <td>
                      <select className="admin-select" value={s.status}
                        onChange={e => updateMutation.mutate({ id: s.id, data: { status: e.target.value } })}>
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="refunded">Refunded</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td><span className={`badge badge-${s.payout_status}`}>{s.payout_status}</span></td>
                    <td style={{ fontSize: 13 }}>{new Date(s.order_date).toLocaleDateString()}</td>
                    <td>
                      {s.status === 'confirmed' && s.payout_status === 'pending' && (
                        <button className="admin-btn admin-btn-approve" disabled={updateMutation.isPending}
                          onClick={() => updateMutation.mutate({ id: s.id, data: { payout_status: 'paid' } })}>
                          Mark Paid
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

/* ═══════════ TASKS TAB (ADMIN ASSIGNMENT) ═══════════ */
function TasksTab() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const { data: tasks, isLoading } = useQuery({
    queryKey: ['admin-tasks'],
    queryFn: () => taskAPI.getAll().then(r => r.data.tasks),
  });

  const { data: users } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => userAPI.getAll().then(r => r.data.users),
  });

  const createMutation = useMutation({
    mutationFn: (data) => taskAPI.create(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admin-tasks'] }); setShowForm(false); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => taskAPI.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-tasks'] }),
  });

  if (isLoading) return <div className="admin-loading">Loading tasks...</div>;

  return (
    <>
      <div className="admin-card">
        <div className="admin-card-header">
          <h2>All Tasks ({tasks?.length || 0})</h2>
          <button className="admin-btn-primary" onClick={() => setShowForm(true)}><Plus size={16} /> Create Task</button>
        </div>
        {!tasks || tasks.length === 0 ? (
          <div className="admin-empty">No tasks yet.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Assigned To</th>
                  <th>Status</th>
                  <th>Points</th>
                  <th>Priority</th>
                  <th>Due</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map(t => (
                  <tr key={t.id}>
                    <td style={{ fontWeight: 500 }}>{t.title}</td>
                    <td><span className="badge badge-ambassador">{t.type}</span></td>
                    <td>{t.assignee_name || <span style={{ color: '#9ca3af' }}>Unassigned</span>}</td>
                    <td><span className={`badge badge-${t.status}`}>{t.status}</span></td>
                    <td style={{ fontWeight: 600, color: '#6366f1' }}>{t.points}</td>
                    <td><span className={`badge badge-${t.priority === 'high' || t.priority === 'urgent' ? 'cancelled' : t.priority === 'medium' ? 'pending' : 'active'}`}>{t.priority}</span></td>
                    <td style={{ fontSize: 13 }}>{t.due_date ? new Date(t.due_date).toLocaleDateString() : '—'}</td>
                    <td>
                      <button className="admin-btn-danger" onClick={() => {
                        if (window.confirm(`Delete "${t.title}"?`)) deleteMutation.mutate(t.id);
                      }}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <TaskForm
          users={users || []}
          onSubmit={(data) => createMutation.mutate(data)}
          onClose={() => setShowForm(false)}
          isSubmitting={createMutation.isPending}
        />
      )}
    </>
  );
}

function TaskForm({ users, onSubmit, onClose, isSubmitting }) {
  const [form, setForm] = useState({
    title: '', description: '', type: 'training', points: 10,
    assigned_to: '', due_date: '', priority: 'medium', visibility: 'team',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ ...form, points: parseInt(form.points), assigned_to: form.assigned_to || null, due_date: form.due_date || null });
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={e => e.stopPropagation()}>
        <h2>Create Task</h2>
        <form onSubmit={handleSubmit}>
          <div className="admin-form-group">
            <label>Title *</label>
            <input name="title" value={form.title} onChange={handleChange} required />
          </div>
          <div className="admin-form-group">
            <label>Description</label>
            <textarea name="description" value={form.description} onChange={handleChange} />
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Type</label>
              <select name="type" value={form.type} onChange={handleChange}>
                <option value="training">Training</option>
                <option value="content_creation">Content Creation</option>
                <option value="social_sharing">Social Sharing</option>
                <option value="review">Review</option>
                <option value="event">Event</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="admin-form-group">
              <label>Points</label>
              <input type="number" name="points" value={form.points} onChange={handleChange} min="0" />
            </div>
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Assign To</label>
              <select name="assigned_to" value={form.assigned_to} onChange={handleChange}>
                <option value="">Unassigned</option>
                {users.filter(u => u.status === 'active').map(u => (
                  <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.role})</option>
                ))}
              </select>
            </div>
            <div className="admin-form-group">
              <label>Due Date</label>
              <input type="date" name="due_date" value={form.due_date} onChange={handleChange} />
            </div>
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Priority</label>
              <select name="priority" value={form.priority} onChange={handleChange}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="admin-form-group">
              <label>Visibility</label>
              <select name="visibility" value={form.visibility} onChange={handleChange}>
                <option value="public">Public</option>
                <option value="team">Team</option>
              </select>
            </div>
          </div>
          <div className="admin-form-actions">
            <button type="button" className="admin-btn" style={{ background: '#f3f4f6', color: '#374151' }} onClick={onClose}>Cancel</button>
            <button type="submit" className="admin-btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ═══════════ BROADCAST TAB ═══════════ */
function BroadcastTab() {
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [sent, setSent] = useState(null);

  const broadcastMutation = useMutation({
    mutationFn: (data) => messageAPI.broadcast(data),
    onSuccess: (res) => {
      setSent(res.data);
      setContent('');
      setName('');
    },
  });

  const handleSend = (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    if (!window.confirm('Send this announcement to ALL active users?')) return;
    broadcastMutation.mutate({ content, name: name || undefined });
  };

  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <h2><Megaphone size={20} /> Broadcast Message</h2>
      </div>
      <div style={{ padding: 20 }}>
        <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 16 }}>
          Send an announcement to all active ambassadors. This creates a group conversation visible by everyone.
        </p>
        <form onSubmit={handleSend}>
          <div className="admin-form-group">
            <label>Conversation Name (optional)</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Weekly Update" />
          </div>
          <div className="admin-form-group">
            <label>Message *</label>
            <textarea value={content} onChange={e => setContent(e.target.value)} required rows={5}
              placeholder="Type your announcement here..." />
          </div>
          <div className="admin-form-actions">
            <button type="submit" className="admin-btn-primary" disabled={broadcastMutation.isPending}>
              {broadcastMutation.isPending ? 'Sending...' : 'Send Broadcast'}
            </button>
          </div>
        </form>
        {sent && (
          <div className="broadcast-success">
            Broadcast sent to {sent.recipients} user{sent.recipients !== 1 ? 's' : ''}.
          </div>
        )}
        {broadcastMutation.isError && (
          <div className="broadcast-error">
            Failed to send broadcast. {broadcastMutation.error?.response?.data?.error || 'Please try again.'}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════ ACTIVITY TAB ═══════════ */
function ActivityTab() {
  const { data: feed, isLoading } = useQuery({
    queryKey: ['admin-activity'],
    queryFn: () => analyticsAPI.getActivity(50).then(r => r.data.feed),
  });

  if (isLoading) return <div className="admin-loading">Loading activity...</div>;

  const typeIcons = {
    signup: <Users size={16} color="#6366f1" />,
    task_completed: <CheckSquare size={16} color="#10b981" />,
    redemption: <Gift size={16} color="#ec4899" />,
    points: <BarChart3 size={16} color="#f59e0b" />,
  };

  const typeColors = {
    signup: '#e0e7ff',
    task_completed: '#d1fae5',
    redemption: '#fce7f3',
    points: '#fef3c7',
  };

  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <h2><Activity size={20} /> Recent Activity</h2>
      </div>
      {!feed || feed.length === 0 ? (
        <div className="admin-empty">No recent activity.</div>
      ) : (
        <div className="activity-feed">
          {feed.map((item, i) => (
            <div key={i} className="activity-item">
              <div className="activity-icon" style={{ background: typeColors[item.type] || '#f3f4f6' }}>
                {typeIcons[item.type] || <Activity size={16} />}
              </div>
              <div className="activity-content">
                <div className="activity-msg">{item.message}</div>
                {item.detail && <div className="activity-detail">{item.detail}</div>}
              </div>
              <div className="activity-time">{timeAgo(item.created_at)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

/* ═══════════ REWARDS TAB ═══════════ */
function RewardsTab() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const { data: rewards, isLoading } = useQuery({
    queryKey: ['admin-rewards'],
    queryFn: () => rewardAPI.adminGetAll().then(r => r.data.rewards),
  });

  const createMutation = useMutation({
    mutationFn: (data) => rewardAPI.create(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admin-rewards'] }); setShowForm(false); },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => rewardAPI.update(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admin-rewards'] }); setShowForm(false); setEditing(null); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => rewardAPI.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-rewards'] }),
  });

  const openEdit = (reward) => { setEditing(reward); setShowForm(true); };
  const openCreate = () => { setEditing(null); setShowForm(true); };

  if (isLoading) return <div className="admin-loading">Loading rewards...</div>;

  return (
    <>
      <div className="admin-card">
        <div className="admin-card-header">
          <h2>Rewards Catalog ({rewards?.length || 0})</h2>
          <button className="admin-btn-primary" onClick={openCreate}><Plus size={16} /> Add Reward</button>
        </div>
        {!rewards || rewards.length === 0 ? (
          <div className="admin-empty">No rewards yet. Create your first reward.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Points</th>
                  <th>Stock</th>
                  <th>Available</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rewards.map(r => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 500 }}>{r.title}</td>
                    <td><span className={`badge badge-${r.type === 'gift_card' ? 'active' : 'ambassador'}`}>{r.type?.replace('_', ' ')}</span></td>
                    <td style={{ fontWeight: 600, color: '#6366f1' }}>{r.points_cost}</td>
                    <td>{r.stock_quantity ?? '∞'}</td>
                    <td><span className={`badge ${r.is_available ? 'badge-active' : 'badge-inactive'}`}>{r.is_available ? 'Yes' : 'No'}</span></td>
                    <td>
                      <div className="admin-actions">
                        <button className="admin-btn-edit" onClick={() => openEdit(r)}>Edit</button>
                        <button className="admin-btn-danger" onClick={() => {
                          if (window.confirm(`Delete "${r.title}"?`)) deleteMutation.mutate(r.id);
                        }}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <RewardForm
          reward={editing}
          onSubmit={(data) => editing ? updateMutation.mutate({ id: editing.id, data }) : createMutation.mutate(data)}
          onClose={() => { setShowForm(false); setEditing(null); }}
          isSubmitting={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </>
  );
}

function RewardForm({ reward, onSubmit, onClose, isSubmitting }) {
  const [form, setForm] = useState({
    title: reward?.title || '',
    description: reward?.description || '',
    type: reward?.type || 'merchandise',
    points_cost: reward?.points_cost || '',
    stock_quantity: reward?.stock_quantity ?? '',
    image_url: reward?.image_url || '',
    is_available: reward?.is_available !== false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...form,
      points_cost: parseInt(form.points_cost),
      stock_quantity: form.stock_quantity !== '' ? parseInt(form.stock_quantity) : null,
    });
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={e => e.stopPropagation()}>
        <h2>{reward ? 'Edit Reward' : 'New Reward'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="admin-form-group">
            <label>Title *</label>
            <input name="title" value={form.title} onChange={handleChange} required />
          </div>
          <div className="admin-form-group">
            <label>Description</label>
            <textarea name="description" value={form.description} onChange={handleChange} />
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Type *</label>
              <select name="type" value={form.type} onChange={handleChange}>
                <option value="merchandise">Merchandise</option>
                <option value="gift_card">Gift Card</option>
                <option value="experience">Experience</option>
                <option value="donation">Donation</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="admin-form-group">
              <label>Points Cost *</label>
              <input type="number" name="points_cost" value={form.points_cost} onChange={handleChange} required min="1" />
            </div>
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Stock (blank = unlimited)</label>
              <input type="number" name="stock_quantity" value={form.stock_quantity} onChange={handleChange} min="0" />
            </div>
            <div className="admin-form-group">
              <label>Image URL</label>
              <input name="image_url" value={form.image_url} onChange={handleChange} placeholder="https://..." />
            </div>
          </div>
          <div className="admin-form-group">
            <label className="admin-form-check">
              <input type="checkbox" name="is_available" checked={form.is_available} onChange={handleChange} />
              Available for redemption
            </label>
          </div>
          <div className="admin-form-actions">
            <button type="button" className="admin-btn" style={{ background: '#f3f4f6', color: '#374151' }} onClick={onClose}>Cancel</button>
            <button type="submit" className="admin-btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : reward ? 'Update Reward' : 'Create Reward'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ═══════════ SETTINGS TAB ═══════════ */
function SettingsTab() {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [successMsg, setSucessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const { data: settings, isLoading } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: () => analyticsAPI.getSettings().then(r => r.data.settings),
  });

  const [localSettings, setLocalSettings] = useState({});

  // sync fetched settings into local state
  React.useEffect(() => {
    if (settings) {
      const map = {};
      settings.forEach(s => { map[s.key] = s.value; });
      setLocalSettings(map);
    }
  }, [settings]);

  const handleChange = (key, value) => {
    setLocalSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSucessMsg('');
    setErrorMsg('');
    try {
      const updates = Object.entries(localSettings).map(([key, value]) => ({ key, value: String(value) }));
      await analyticsAPI.updateSettings(updates);
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
      setSucessMsg('Settings saved successfully!');
      setTimeout(() => setSucessMsg(''), 3000);
    } catch (err) {
      setErrorMsg(err.response?.data?.error || 'Failed to save settings');
      setTimeout(() => setErrorMsg(''), 5000);
    }
    setSaving(false);
  };

  if (isLoading) return <div className="admin-loading">Loading settings...</div>;
  if (!settings) return null;

  // group by category
  const grouped = {};
  settings.forEach(s => {
    if (!grouped[s.category]) grouped[s.category] = [];
    grouped[s.category].push(s);
  });

  const categoryLabels = {
    commission: 'Commission & Payouts',
    points: 'Points & Rewards',
    moderation: 'Moderation',
    tiers: 'Tier Thresholds',
    general: 'General',
  };

  return (
    <>
      {successMsg && <div className="broadcast-success">{successMsg}</div>}
      {errorMsg && <div className="admin-error" style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', padding: '12px 16px', borderRadius: 8, marginBottom: 16 }}>{errorMsg}</div>}
      <div className="settings-container">
        {Object.entries(grouped).map(([category, items]) => (
          <div key={category} className="settings-section">
            <h3 className="settings-section-title">{categoryLabels[category] || category}</h3>
            <div className="settings-grid">
              {items.map(setting => (
                <div key={setting.key} className="settings-field">
                  <label className="settings-label">
                    {setting.label}
                    {setting.description && <span className="settings-desc">{setting.description}</span>}
                  </label>
                  {setting.type === 'boolean' ? (
                    <label className="settings-toggle">
                      <input
                        type="checkbox"
                        checked={localSettings[setting.key] === 'true' || localSettings[setting.key] === true}
                        onChange={e => handleChange(setting.key, e.target.checked ? 'true' : 'false')}
                      />
                      <span className="settings-toggle-slider"></span>
                    </label>
                  ) : setting.type === 'number' ? (
                    <input
                      type="number"
                      className="settings-input"
                      value={localSettings[setting.key] || ''}
                      onChange={e => handleChange(setting.key, e.target.value)}
                      step={setting.key.includes('rate') ? '0.01' : '1'}
                    />
                  ) : (
                    <input
                      type="text"
                      className="settings-input"
                      value={localSettings[setting.key] || ''}
                      onChange={e => handleChange(setting.key, e.target.value)}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="settings-save-bar">
        <button className="admin-btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save All Settings'}
        </button>
      </div>
    </>
  );
}

/* ═══════════ CHARTS TAB ═══════════ */
function ChartsTab() {
  const { data: signups } = useQuery({
    queryKey: ['chart-signups'],
    queryFn: () => analyticsAPI.getSignupChart().then(r => r.data.signups),
  });
  const { data: points } = useQuery({
    queryKey: ['chart-points'],
    queryFn: () => analyticsAPI.getPointsChart().then(r => r.data.points),
  });
  const { data: topPerformers } = useQuery({
    queryKey: ['chart-top-performers'],
    queryFn: () => analyticsAPI.getTopPerformers().then(r => r.data.performers),
  });
  const { data: tiers } = useQuery({
    queryKey: ['chart-tiers'],
    queryFn: () => analyticsAPI.getTierChart().then(r => r.data.tiers),
  });

  const maxSignup = signups ? Math.max(...signups.map(s => parseInt(s.count) || 0), 1) : 1;
  const maxPoints = points ? Math.max(...points.map(p => Math.max(parseInt(p.earned) || 0, parseInt(p.spent) || 0)), 1) : 1;
  const maxPerformer = topPerformers ? Math.max(...topPerformers.map(p => parseInt(p.total_points) || 0), 1) : 1;
  const totalTierUsers = tiers ? tiers.reduce((sum, t) => sum + (parseInt(t.count) || 0), 0) : 1;

  const tierColors = { bronze: '#cd7f32', silver: '#94a3b8', gold: '#f59e0b', platinum: '#8b5cf6' };

  const formatDate = (d) => {
    const date = new Date(d);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  return (
    <div className="charts-container">
      {/* Signups Chart */}
      <div className="admin-card chart-card">
        <div className="admin-card-header"><h2>User Signups (Last 30 Days)</h2></div>
        <div className="chart-bar-container">
          {signups ? signups.map((s, i) => (
            <div key={i} className="chart-bar-col">
              <div className="chart-bar-value">{s.count}</div>
              <div className="chart-bar" style={{ height: `${(parseInt(s.count) / maxSignup) * 120}px`, background: '#6366f1' }}></div>
              <div className="chart-bar-label">{formatDate(s.day)}</div>
            </div>
          )) : <div className="admin-loading">Loading...</div>}
        </div>
      </div>

      {/* Points Earned vs Spent */}
      <div className="admin-card chart-card">
        <div className="admin-card-header">
          <h2>Points Earned vs Spent (Last 30 Days)</h2>
          <div className="chart-legend">
            <span className="chart-legend-item"><span className="chart-dot" style={{ background: '#10b981' }}></span>Earned</span>
            <span className="chart-legend-item"><span className="chart-dot" style={{ background: '#ef4444' }}></span>Spent</span>
          </div>
        </div>
        <div className="chart-bar-container">
          {points ? points.map((p, i) => (
            <div key={i} className="chart-bar-col dual">
              <div className="chart-bar-pair">
                <div className="chart-bar" style={{ height: `${(parseInt(p.earned || 0) / maxPoints) * 120}px`, background: '#10b981' }}></div>
                <div className="chart-bar" style={{ height: `${(parseInt(p.spent || 0) / maxPoints) * 120}px`, background: '#ef4444' }}></div>
              </div>
              <div className="chart-bar-label">{formatDate(p.day)}</div>
            </div>
          )) : <div className="admin-loading">Loading...</div>}
        </div>
      </div>

      {/* Top Performers */}
      <div className="admin-card chart-card">
        <div className="admin-card-header"><h2>Top 10 Performers</h2></div>
        <div className="chart-horizontal">
          {topPerformers ? topPerformers.map((p, i) => (
            <div key={i} className="chart-h-row">
              <div className="chart-h-label">{p.first_name} {p.last_name}</div>
              <div className="chart-h-bar-wrap">
                <div className="chart-h-bar" style={{ width: `${(parseInt(p.total_points) / maxPerformer) * 100}%`, background: `hsl(${240 - i * 18}, 70%, 60%)` }}>
                  <span>{parseInt(p.total_points).toLocaleString()} pts</span>
                </div>
              </div>
            </div>
          )) : <div className="admin-loading">Loading...</div>}
        </div>
      </div>

      {/* Tier Distribution */}
      <div className="admin-card chart-card">
        <div className="admin-card-header"><h2>Ambassador Tier Distribution</h2></div>
        <div className="chart-tier-grid">
          {tiers ? tiers.map((t, i) => (
            <div key={i} className="chart-tier-card" style={{ borderLeftColor: tierColors[t.tier] || '#6b7280' }}>
              <div className="chart-tier-name">{t.tier}</div>
              <div className="chart-tier-count">{t.count}</div>
              <div className="chart-tier-bar-wrap">
                <div className="chart-tier-bar" style={{ width: `${(parseInt(t.count) / totalTierUsers) * 100}%`, background: tierColors[t.tier] || '#6b7280' }}></div>
              </div>
              <div className="chart-tier-pct">{((parseInt(t.count) / totalTierUsers) * 100).toFixed(1)}%</div>
            </div>
          )) : <div className="admin-loading">Loading...</div>}
        </div>
      </div>
    </div>
  );
}

/* ═══════════ ANALYTICS TAB ═══════════ */
function AnalyticsTab({ analytics }) {
  if (!analytics) return <div className="admin-loading">Loading analytics...</div>;

  const usersByStatus = {};
  analytics.users?.forEach(u => { usersByStatus[u.status] = parseInt(u.count) || 0; });
  const totalUsers = Object.values(usersByStatus).reduce((a, b) => a + b, 0);

  return (
    <>
      <div className="admin-stats">
        <div className="admin-stat-card">
          <div className="admin-stat-icon" style={{ background: '#e0e7ff' }}>
            <Users size={22} color="#6366f1" />
          </div>
          <div>
            <div className="admin-stat-val">{totalUsers}</div>
            <div className="admin-stat-lbl">Total Users</div>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon" style={{ background: '#d1fae5' }}>
            <BarChart3 size={22} color="#10b981" />
          </div>
          <div>
            <div className="admin-stat-val">{analytics.campaigns?.active || 0}</div>
            <div className="admin-stat-lbl">Active Campaigns</div>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon" style={{ background: '#fef3c7' }}>
            <FileCheck size={22} color="#f59e0b" />
          </div>
          <div>
            <div className="admin-stat-val">{analytics.tasks?.completed || 0}</div>
            <div className="admin-stat-lbl">Tasks Completed</div>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon" style={{ background: '#fce7f3' }}>
            <Gift size={22} color="#ec4899" />
          </div>
          <div>
            <div className="admin-stat-val">{analytics.tasks?.points_awarded || 0}</div>
            <div className="admin-stat-lbl">Points Awarded</div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div className="admin-card">
          <div className="admin-card-header"><h2>Users by Status</h2></div>
          <table className="admin-table">
            <thead><tr><th>Status</th><th>Count</th></tr></thead>
            <tbody>
              {Object.entries(usersByStatus).map(([status, count]) => (
                <tr key={status}>
                  <td><span className={`badge badge-${status}`}>{status}</span></td>
                  <td style={{ fontWeight: 600 }}>{count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="admin-card">
          <div className="admin-card-header"><h2>Sales & Revenue</h2></div>
          <table className="admin-table">
            <thead><tr><th>Metric</th><th>Value</th></tr></thead>
            <tbody>
              <tr><td>Total Sales</td><td style={{ fontWeight: 600 }}>{analytics.sales?.count || 0}</td></tr>
              <tr><td>Revenue</td><td style={{ fontWeight: 600 }}>${Number(analytics.sales?.revenue || 0).toFixed(2)}</td></tr>
              <tr><td>Commissions Paid</td><td style={{ fontWeight: 600 }}>${Number(analytics.sales?.commissions || 0).toFixed(2)}</td></tr>
            </tbody>
          </table>
        </div>

        <div className="admin-card" style={{ gridColumn: '1 / -1' }}>
          <div className="admin-card-header"><h2>Social Engagement</h2></div>
          <div className="admin-stats" style={{ marginBottom: 0 }}>
            {[
              { label: 'Posts', value: analytics.social?.posts || 0 },
              { label: 'Likes', value: analytics.social?.likes || 0 },
              { label: 'Comments', value: analytics.social?.comments || 0 },
              { label: 'Shares', value: analytics.social?.shares || 0 },
              { label: 'Clicks', value: analytics.social?.clicks || 0 },
              { label: 'Impressions', value: analytics.social?.impressions || 0 },
            ].map(s => (
              <div key={s.label} style={{ textAlign: 'center', padding: '12px 0' }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}>{s.value}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

export default Admin;
